import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CommentWithUser, CommentStatus, PendingComment } from '@/types/comments';
import { useToast } from '@/components/ui/use-toast';

const COMMENTS_PER_PAGE = 10;

// Type for what the SQL function returns
interface CommentFromDB {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  user_name: string | null;
  user_avatar: string | null;
  username: string | null;
  belt_rank?: string | null;
}

export function usePostComments(postId: string) {
  const [comments, setComments] = useState<CommentWithUser[]>([]);
  const [commentCount, setCommentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pendingComments, setPendingComments] = useState<PendingComment[]>([]);
  const { user } = useAuth();
  const { toast } = useToast();

  // Load initial comments and count
  useEffect(() => {
    if (user && postId) {
      loadCommentCount();
      loadComments();
    }
  }, [user, postId]);

  const loadCommentCount = async () => {
    try {
      const { data, error } = await supabase.rpc('get_post_comment_count', {
        post_uuid: postId
      });

      if (error) throw error;
      setCommentCount(data || 0);
    } catch (error) {
      console.error('Error loading comment count:', error);
    }
  };

  const loadComments = async (page = 0) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_post_comments', {
        post_uuid: postId,
        page_size: COMMENTS_PER_PAGE,
        page_offset: page * COMMENTS_PER_PAGE
      });

      if (error) throw error;

      // Transform the data to match CommentWithUser type
      const transformedComments: CommentWithUser[] = (data || []).map((comment: CommentFromDB) => ({
        id: comment.id,
        post_id: postId,
        user_id: comment.user_id,
        content: comment.content,
        created_at: comment.created_at,
        updated_at: comment.created_at, // Use created_at as updated_at since we don't have it
        user_name: comment.user_name,
        user_avatar: comment.user_avatar,
        username: comment.username || null,
        belt_rank: comment.belt_rank || 'white',
      }));

      if (page === 0) {
        setComments(transformedComments);
      } else {
        setComments(prev => [...prev, ...transformedComments]);
      }

      setHasMore((data || []).length === COMMENTS_PER_PAGE);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreComments = useCallback(() => {
    const currentPage = Math.floor(comments.length / COMMENTS_PER_PAGE);
    loadComments(currentPage);
  }, [comments.length]);

  const addComment = async (content: string) => {
    if (!user) {
      window.location.href = '/?tab=signin';
      return;
    }

    // Create pending comment
    const pendingId = `pending-${Date.now()}`;
    const pendingComment: PendingComment = {
      id: pendingId,
      content,
      post_id: postId,
      status: 'pending'
    };

    setPendingComments(prev => [...prev, pendingComment]);

    try {
      const { data, error } = await supabase.rpc('create_post_comment', {
        post_uuid: postId,
        comment_content: content
      });

      if (error) throw error;

      // Remove all pending comments with the same content (to avoid duplicates/loading)
      setPendingComments(prev => prev.filter(c => c.content !== content));

      // Reload comments and count
      await loadCommentCount();
      await loadComments(0);

    } catch (error) {
      console.error('Error creating comment:', error);
      
      // Mark as failed
      setPendingComments(prev => 
        prev.map(c => 
          c.id === pendingId 
            ? { ...c, status: 'failed', error: error instanceof Error ? error.message : 'Failed to post comment' }
            : c
        )
      );
    }
  };

  const retryComment = async (pendingComment: PendingComment) => {
    // Update status to 'pending' and clear error
    setPendingComments(prev => 
      prev.map(c => 
        c.id === pendingComment.id 
          ? { ...c, status: 'pending', error: undefined }
          : c
      )
    );

    try {
      const { data, error } = await supabase.rpc('create_post_comment', {
        post_uuid: postId,
        comment_content: pendingComment.content
      });
      if (error) throw error;
      // Remove all pending comments with the same content
      setPendingComments(prev => prev.filter(c => c.content !== pendingComment.content));
      // Reload comments and count
      await loadCommentCount();
      await loadComments(0);
    } catch (error) {
      // Mark as failed
      setPendingComments(prev =>
        prev.map(c =>
          c.id === pendingComment.id
            ? { ...c, status: 'failed', error: error instanceof Error ? error.message : 'Failed to post comment' }
            : c
        )
      );
    }
  };

  const removePendingComment = (pendingId: string) => {
    setPendingComments(prev => prev.filter(c => c.id !== pendingId));
  };

  const deleteComment = async (commentId: string) => {
    // Optimistically remove comment
    const prevComments = comments;
    setComments(comments.filter(c => c.id !== commentId));
    setCommentCount((count) => Math.max(0, count - 1));
    try {
      const { error } = await supabase.rpc('delete_post_comment', {
        comment_uuid: commentId
      });
      if (error) throw error;
    } catch (error) {
      // Restore comment and count
      setComments(prevComments);
      setCommentCount((count) => count + 1);
      toast({
        title: 'Failed to delete comment',
        description: 'Failed to delete comment. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return {
    comments,
    commentCount,
    isLoading,
    hasMore,
    pendingComments,
    loadMoreComments,
    addComment,
    retryComment,
    removePendingComment,
    canComment: !!user,
    deleteComment,
  };
} 